import type { MarketplaceModel, AdminModel, ModelLicense, ActiveModelState, AuthHeadersFn } from './marketplaceTypes';
import { API_URL } from '../../config';

async function fetchJson<T>(url: string, headers: HeadersInit): Promise<T> {
  const response = await fetch(url, { headers });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
    throw new Error(err.detail || `Request failed: ${response.status}`);
  }
  return response.json();
}

async function mutate<T>(url: string, method: string, headers: HeadersInit, body?: unknown): Promise<T> {
  const response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
    throw new Error(err.detail || `Request failed: ${response.status}`);
  }
  return response.json();
}

// User-facing
export async function fetchModels(h: AuthHeadersFn, category?: string): Promise<MarketplaceModel[]> {
  const sp = new URLSearchParams();
  if (category) sp.set('category', category);
  const qs = sp.toString();
  return fetchJson(`${API_URL}/api/marketplace/models${qs ? '?' + qs : ''}`, h());
}

export async function fetchModelBySlug(h: AuthHeadersFn, slug: string): Promise<MarketplaceModel> {
  return fetchJson(`${API_URL}/api/marketplace/models/${slug}`, h());
}

export async function fetchMyModels(h: AuthHeadersFn): Promise<MarketplaceModel[]> {
  return fetchJson(`${API_URL}/api/marketplace/my-models`, h());
}

// Live-feed activation
export async function fetchActiveModel(h: AuthHeadersFn): Promise<ActiveModelState> {
  return fetchJson(`${API_URL}/api/marketplace/active`, h());
}

export async function activateModel(h: AuthHeadersFn, slug: string): Promise<{ status: string; slug: string }> {
  return mutate(`${API_URL}/api/marketplace/models/${slug}/activate`, 'POST', h());
}

export async function deactivateModel(h: AuthHeadersFn): Promise<{ status: string }> {
  return mutate(`${API_URL}/api/marketplace/deactivate`, 'POST', h());
}

// Admin
export async function fetchAdminModels(h: AuthHeadersFn): Promise<AdminModel[]> {
  return fetchJson(`${API_URL}/api/marketplace/admin/models`, h());
}

export async function createModel(h: AuthHeadersFn, data: Partial<AdminModel>): Promise<AdminModel> {
  return mutate(`${API_URL}/api/marketplace/admin/models`, 'POST', h(true), data);
}

export async function updateModel(h: AuthHeadersFn, id: number, data: Partial<AdminModel>): Promise<AdminModel> {
  return mutate(`${API_URL}/api/marketplace/admin/models/${id}`, 'PUT', h(true), data);
}

export async function deleteModel(h: AuthHeadersFn, id: number): Promise<void> {
  return mutate(`${API_URL}/api/marketplace/admin/models/${id}`, 'DELETE', h());
}

export async function fetchModelLicenses(h: AuthHeadersFn, modelId: number): Promise<ModelLicense[]> {
  return fetchJson(`${API_URL}/api/marketplace/admin/models/${modelId}/licenses`, h());
}

export async function grantLicense(h: AuthHeadersFn, modelId: number, data: {
  user_id: number;
  license_type: 'perpetual' | 'cloud' | 'rental';
  expires_at?: string;
  notes?: string;
}): Promise<ModelLicense> {
  return mutate(`${API_URL}/api/marketplace/admin/models/${modelId}/licenses`, 'POST', h(true), data);
}

export async function revokeLicense(h: AuthHeadersFn, licenseId: number): Promise<void> {
  return mutate(`${API_URL}/api/marketplace/admin/licenses/${licenseId}`, 'DELETE', h());
}
